import React from 'react';
import { Navigate } from 'react-router-dom';
import { redirectUnlessInventory } from '../../utils/moduleAccess';
import { Form, FormGroup, FormControl, Button, Row, Col } from 'react-bootstrap';
import config from '../../../config';
import { handleFocusInScrollField } from './invKeyboardScroll';
import {
  ensureSharedTableStyles,
  SHARED_GRID_TABLE_SCROLL_WRAP_CLASS,
} from '../../_components/Shared/dataTableStyles';

class ActivateStorage extends React.Component {
    constructor(props) {
        super(props);
        // Determine storage type from route or default to RM
        const path = window.location.pathname;
        let defaultType = 'RM';
        if (path.includes('activate-fg-storage')) {
            defaultType = 'FG';
        } else if (path.includes('activate-rm-storage')) {
            defaultType = 'RM';
        }
        
        this.state = {
            storageType: defaultType, // 'RM' for Raw Material, 'FG' for Finished Goods
            area: '', // 'RM' for Raw Material, 'FG' for Finished Goods, 'QT' for Quarantine
            location: '', // Selected location
            locations: [], // List of locations filtered by area
            row: '', // 1-10
            selectedCells: new Set(), // Track selected cells (saved in database)
            pendingSelections: new Set(), // Track pending selections (not yet saved)
        };
        
        // Track the last fetched location/row combination to prevent infinite loops
        this.lastFetchedKey = null;
        this.isFetching = false;
    }

    componentDidMount() {
        ensureSharedTableStyles();
        // Load data when component mounts
        // Update storage type if route changes
        const path = window.location.pathname;
        if (path.includes('activate-fg-storage')) {
            this.setState({ storageType: 'FG' });
        } else if (path.includes('activate-rm-storage')) {
            this.setState({ storageType: 'RM' });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // Fetch storage locations when both location and row are selected
        const { location, row, area } = this.state;
        const currentKey = location && row ? `${location}-${row}` : null;
        
        // Only fetch if location or row changed (not if selectedCells changed)
        if (currentKey && currentKey !== this.lastFetchedKey && !this.isFetching) {
            if (prevState.location !== location || prevState.row !== row) {
                this.lastFetchedKey = currentKey;
                this.fetchStorageLocations(location, row);
            }
        }
        
        // Clear selections when location or row is cleared
        if ((prevState.location || prevState.row) && (!location || !row)) {
            this.lastFetchedKey = null;
            if (this.state.selectedCells.size > 0 || this.state.pendingSelections.size > 0) {
                this.setState({ selectedCells: new Set(), pendingSelections: new Set() });
            }
        }
    }

    fetchLocations(area) {
        if (!area) {
            this.setState({ locations: [], location: '' });
            return;
        }

        const requestOptions = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        };

        if (localStorage.getItem('user') != null) {
            const user = JSON.parse(localStorage.getItem('user'));
            // Map area values to actual area names used in the API
            let areaName = area;
            if (area === 'RM') {
                areaName = 'RM Warehouse';
            } else if (area === 'FG') {
                areaName = 'FG Warehouse';
            } else if (area === 'QT') {
                areaName = 'Quarantine';
            }

            fetch(`${config.apiUrl}/getMasterLocations?company=${user.accountId}&area=${encodeURIComponent(areaName)}`, requestOptions)
                .then((response) => {
                    return response.text().then(text => {
                        const data = text && JSON.parse(text);
                        return data;
                    });
                })
                .then(locations => {
                    if (locations && Array.isArray(locations)) {
                        const auto =
                            locations.length === 1 && locations[0].locationCode
                                ? locations[0].locationCode
                                : '';
                        this.setState({ locations: locations, location: auto });
                    } else {
                        this.setState({ locations: [], location: '' });
                    }
                })
                .catch(error => {
                    console.error('Error fetching locations:', error);
                    this.setState({ locations: [], location: '' });
                });
        }
    }

    handleCellClick(level, pallet) {
        const { area, row, selectedCells, pendingSelections } = this.state;
        const cellKey = `${level}-${pallet}`;
        const newPendingSelections = new Set(pendingSelections);
        const isCurrentlySelected = selectedCells.has(cellKey);
        const isPending = newPendingSelections.has(cellKey);
        
        // If already saved in database, remove it
        if (isCurrentlySelected) {
            this.deleteStorageLocation(this.state.location, row, level, pallet);
            const newSelectedCells = new Set(selectedCells);
            newSelectedCells.delete(cellKey);
            this.setState({ selectedCells: newSelectedCells });
        } 
        // If pending, remove from pending
        else if (isPending) {
            newPendingSelections.delete(cellKey);
            this.setState({ pendingSelections: newPendingSelections });
        } 
        // Otherwise, add to pending
        else {
            newPendingSelections.add(cellKey);
            this.setState({ pendingSelections: newPendingSelections });
        }
        
        const label = `${area}${row}.${level}.${pallet}`;
        console.log('Cell clicked:', label);
    }

    handleColumnClick(pallet) {
        const { location, row, selectedCells, pendingSelections } = this.state;
        const levels = [1, 2, 3, 4, 5];
        const columnCells = levels.map(level => `${level}-${pallet}`);
        const newPendingSelections = new Set(pendingSelections);
        const newSelectedCells = new Set(selectedCells);
        
        // Check if all cells in this column are selected (saved) or pending
        const allSelected = columnCells.every(cellKey => selectedCells.has(cellKey));
        const allPending = columnCells.every(cellKey => pendingSelections.has(cellKey));
        
        if (allSelected) {
            // Delete all saved cells in this column
            columnCells.forEach(cellKey => newSelectedCells.delete(cellKey));
            levels.forEach(level => {
                this.deleteStorageLocation(location, row, level, pallet);
            });
            this.setState({ selectedCells: newSelectedCells });
        } else if (allPending) {
            // Remove all pending cells in this column
            columnCells.forEach(cellKey => newPendingSelections.delete(cellKey));
            this.setState({ pendingSelections: newPendingSelections });
        } else {
            // Add all cells in this column to pending (only if not already saved)
            columnCells.forEach(cellKey => {
                if (!selectedCells.has(cellKey)) {
                    newPendingSelections.add(cellKey);
                }
            });
            this.setState({ pendingSelections: newPendingSelections });
        }
    }

    handleRowClick(level) {
        const { location, row, selectedCells, pendingSelections } = this.state;
        const pallets = Array.from({ length: 20 }, (_, i) => i + 1);
        const rowCells = pallets.map(pallet => `${level}-${pallet}`);
        const newPendingSelections = new Set(pendingSelections);
        const newSelectedCells = new Set(selectedCells);
        
        // Check if all cells in this row are selected (saved) or pending
        const allSelected = rowCells.every(cellKey => selectedCells.has(cellKey));
        const allPending = rowCells.every(cellKey => pendingSelections.has(cellKey));
        
        if (allSelected) {
            // Delete all saved cells in this row
            rowCells.forEach(cellKey => newSelectedCells.delete(cellKey));
            pallets.forEach(pallet => {
                this.deleteStorageLocation(location, row, level, pallet);
            });
            this.setState({ selectedCells: newSelectedCells });
        } else if (allPending) {
            // Remove all pending cells in this row
            rowCells.forEach(cellKey => newPendingSelections.delete(cellKey));
            this.setState({ pendingSelections: newPendingSelections });
        } else {
            // Add all cells in this row to pending (only if not already saved)
            rowCells.forEach(cellKey => {
                if (!selectedCells.has(cellKey)) {
                    newPendingSelections.add(cellKey);
                }
            });
            this.setState({ pendingSelections: newPendingSelections });
        }
    }

    handleSelectAll() {
        const { location, row, selectedCells, pendingSelections } = this.state;
        const levels = [1, 2, 3, 4, 5];
        const pallets = Array.from({ length: 20 }, (_, i) => i + 1);
        const totalCells = levels.length * pallets.length;
        const currentSelected = selectedCells.size;
        const currentPending = pendingSelections.size;
        
        if (currentSelected === totalCells) {
            // Delete all saved cells
            this.setState({ selectedCells: new Set(), pendingSelections: new Set() });
            levels.forEach(level => {
                pallets.forEach(pallet => {
                    this.deleteStorageLocation(location, row, level, pallet);
                });
            });
        } else {
            // Add all unsaved cells to pending
            const newPendingSelections = new Set(pendingSelections);
            levels.forEach(level => {
                pallets.forEach(pallet => {
                    const cellKey = `${level}-${pallet}`;
                    if (!selectedCells.has(cellKey)) {
                        newPendingSelections.add(cellKey);
                    }
                });
            });
            this.setState({ pendingSelections: newPendingSelections });
        }
    }

    handleSave() {
        const { location, row, pendingSelections } = this.state;
        if (pendingSelections.size === 0) {
            alert('No parcels selected to save.');
            return;
        }

        const levels = [1, 2, 3, 4, 5];
        const pallets = Array.from({ length: 20 }, (_, i) => i + 1);
        const newSelectedCells = new Set(this.state.selectedCells);
        let saveCount = 0;
        let errorCount = 0;

        // Save all pending selections
        const savePromises = [];
        pendingSelections.forEach(cellKey => {
            const [level, pallet] = cellKey.split('-').map(Number);
            savePromises.push(
                this.saveStorageLocationPromise(location, row, level, pallet)
                    .then(() => {
                        newSelectedCells.add(cellKey);
                        saveCount++;
                    })
                    .catch(() => {
                        errorCount++;
                    })
            );
        });

        Promise.all(savePromises).then(() => {
            this.setState({ 
                selectedCells: newSelectedCells, 
                pendingSelections: new Set() 
            });
            if (errorCount === 0) {
                alert(`Successfully saved ${saveCount} parcel(s).`);
            } else {
                alert(`Saved ${saveCount} parcel(s), but ${errorCount} failed.`);
            }
        });
    }

    saveStorageLocationPromise(locationCode, row, level, pallet) {
        return new Promise((resolve, reject) => {
            if (!localStorage.getItem('user')) {
                reject(new Error('User not logged in'));
                return;
            }

            const user = JSON.parse(localStorage.getItem('user'));
            const storageCode = `${this.state.area}${row}.${level}.${pallet}`;

            fetch(`${config.apiUrl}/createStorageLocation`, {
                method: "post",
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    locationCode,
                    row: parseInt(row),
                    level: parseInt(level),
                    pallet: parseInt(pallet),
                    storageCode,
                    area: this.state.area,
                    company: user.accountId
                })
            })
                .then((response) => {
                    if (response.status === 200) {
                        resolve();
                    } else {
                        return response.json().then(data => {
                            reject(new Error(data.error || 'Failed to save'));
                        });
                    }
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    saveStorageLocation(locationCode, row, level, pallet) {
        if (!localStorage.getItem('user')) return;

        const user = JSON.parse(localStorage.getItem('user'));
        const storageCode = `${this.state.area}${row}.${level}.${pallet}`;

        fetch(`${config.apiUrl}/createStorageLocation`, {
            method: "post",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                locationCode,
                row: parseInt(row),
                level: parseInt(level),
                pallet: parseInt(pallet),
                storageCode,
                area: this.state.area,
                company: user.accountId
            })
        })
            .then((response) => {
                if (response.status === 200) {
                    console.log('Storage location saved:', storageCode);
                } else {
                    return response.json().then(data => {
                        console.error('Error saving storage location:', data.error || 'Failed to save');
                    });
                }
            })
            .catch(error => {
                console.error('Error saving storage location:', error);
            });
    }

    deleteStorageLocation(locationCode, row, level, pallet) {
        if (!localStorage.getItem('user')) return;

        const user = JSON.parse(localStorage.getItem('user'));

        fetch(`${config.apiUrl}/deleteStorageLocation`, {
            method: "post",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                locationCode,
                row: parseInt(row),
                level: parseInt(level),
                pallet: parseInt(pallet),
                company: user.accountId
            })
        })
            .then((response) => {
                if (response.status === 200) {
                    console.log('Storage location deleted');
                } else {
                    return response.json().then(data => {
                        console.error('Error deleting storage location:', data.error || 'Failed to delete');
                    });
                }
            })
            .catch(error => {
                console.error('Error deleting storage location:', error);
            });
    }

    fetchStorageLocations(locationCode, row) {
        if (!locationCode || !row || !localStorage.getItem('user')) {
            if (this.state.selectedCells.size > 0) {
                this.setState({ selectedCells: new Set() });
            }
            this.isFetching = false;
            return;
        }

        this.isFetching = true;
        const requestOptions = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        };

        const user = JSON.parse(localStorage.getItem('user'));
        fetch(`${config.apiUrl}/getStorageLocations?company=${user.accountId}&locationCode=${encodeURIComponent(locationCode)}&row=${row}`, requestOptions)
            .then((response) => {
                // Handle 404 or other errors gracefully
                if (response.status === 404) {
                    // Endpoint doesn't exist yet, return empty array
                    return [];
                }
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.text().then(text => {
                    if (!text || text.trim() === '') {
                        return [];
                    }
                    try {
                        const data = JSON.parse(text);
                        return data;
                    } catch (e) {
                        // If response is not JSON (e.g., HTML error page), return empty array
                        console.warn('Response is not valid JSON, returning empty array');
                        return [];
                    }
                });
            })
            .then(storageLocations => {
                this.isFetching = false;
                if (storageLocations && Array.isArray(storageLocations)) {
                    const selectedCells = new Set();
                    storageLocations.forEach(loc => {
                        const cellKey = `${loc.level}-${loc.pallet}`;
                        selectedCells.add(cellKey);
                    });
                    // Only update if the current key still matches (prevent race conditions)
                    const currentKey = this.state.location && this.state.row ? `${this.state.location}-${this.state.row}` : null;
                    if (currentKey === this.lastFetchedKey) {
                        this.setState({ selectedCells: selectedCells });
                    }
                } else {
                    const currentKey = this.state.location && this.state.row ? `${this.state.location}-${this.state.row}` : null;
                    if (currentKey === this.lastFetchedKey) {
                        this.setState({ selectedCells: new Set() });
                    }
                }
            })
            .catch(error => {
                this.isFetching = false;
                // Silently handle 404 or other errors - endpoint may not exist yet
                console.warn('Error fetching storage locations (endpoint may not exist yet):', error.message);
                const currentKey = this.state.location && this.state.row ? `${this.state.location}-${this.state.row}` : null;
                if (currentKey === this.lastFetchedKey) {
                    this.setState({ selectedCells: new Set() });
                }
            });
    }

    render() {
        const accessDenied = redirectUnlessInventory(localStorage.getItem('user'));
        if (accessDenied) return accessDenied;

        const { storageType, area, location, locations, row, selectedCells, pendingSelections } = this.state;
        const storageTypeLabel = storageType === 'RM' ? 'Raw Material' : 'Finished Goods';

        // Generate row options (1-10)
        const rowOptions = [];
        for (let i = 1; i <= 10; i++) {
            rowOptions.push(i);
        }

        // Check if table should be shown (both location and row must be selected)
        const showTable = location && row && area;

        // Find the selected location object to get its description
        const selectedLocationObj = locations.find(loc => loc.locationCode === location);
        const locationDescription = selectedLocationObj ? (selectedLocationObj.locationDescription || selectedLocationObj.locationCode) : location;

        // Generate table cells: 5 levels (rows) x 20 pallets (columns)
        const levels = [1, 2, 3, 4, 5];
        const pallets = Array.from({ length: 20 }, (_, i) => i + 1);

        return (
            <div className="container" onFocusCapture={handleFocusInScrollField}>
                <div style={{ marginTop: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div>
                            <h4>Activate Storage</h4>
                            <p className="text-muted">Activate and manage storage locations</p>
                        </div>
                    </div>
                    
                    {/* Form with Area and Row dropdowns */}
                    <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                        <Form>
                            <Row>
                                <Col sm={6} md={4}>
                                    <FormGroup className="row" as={Row} style={{ marginBottom: '15px' }}>
                                        <Col sm={4}><Form.Label>Area</Form.Label></Col>
                                        <Col sm={8}>
                                            <FormControl
                                                as="select"
                                                value={area}
                                                onChange={(e) => {
                                                    const selectedArea = e.target.value;
                                                    this.lastFetchedKey = null;
                                                    this.setState({ area: selectedArea, selectedCells: new Set(), pendingSelections: new Set() });
                                                    this.fetchLocations(selectedArea);
                                                }}
                                            >
                                                <option value="">Select Area</option>
                                                <option value="RM">Raw Material (RM)</option>
                                                <option value="FG">Finished Goods (FG)</option>
                                                <option value="QT">Quarantine (QT)</option>
                                            </FormControl>
                                        </Col>
                                    </FormGroup>
                                </Col>
                                <Col sm={6} md={4}>
                                    <FormGroup className="row" as={Row} style={{ marginBottom: '15px' }}>
                                        <Col sm={4}><Form.Label>Location</Form.Label></Col>
                                        <Col sm={8}>
                                            <FormControl
                                                as="select"
                                                value={location}
                                                onChange={(e) => {
                                                    this.lastFetchedKey = null;
                                                    this.setState({ location: e.target.value, selectedCells: new Set(), pendingSelections: new Set() });
                                                }}
                                                disabled={!area || locations.length === 0}
                                            >
                                                <option value="">Select Location</option>
                                                {locations.map((loc) => (
                                                    <option key={loc.id} value={loc.locationCode}>
                                                        {loc.locationDescription || loc.locationCode}
                                                    </option>
                                                ))}
                                            </FormControl>
                                        </Col>
                                    </FormGroup>
                                </Col>
                                <Col sm={6} md={4}>
                                    <FormGroup className="row" as={Row} style={{ marginBottom: '15px' }}>
                                        <Col sm={4}><Form.Label>Row</Form.Label></Col>
                                        <Col sm={8}>
                                            <FormControl
                                                as="select"
                                                value={row}
                                                onChange={(e) => {
                                                    this.lastFetchedKey = null;
                                                    this.setState({ row: e.target.value, selectedCells: new Set(), pendingSelections: new Set() });
                                                }}
                                            >
                                                <option value="">Select Row</option>
                                                {rowOptions.map((rowNum) => (
                                                    <option key={rowNum} value={rowNum}>{rowNum}</option>
                                                ))}
                                            </FormControl>
                                        </Col>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Form>
                    </div>

                    {/* Clickable Storage Table */}
                    {showTable && (
                        <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#ffffff', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <h5 style={{ 
                                        margin: 0, 
                                        fontWeight: 600, 
                                        color: '#2c3e50', 
                                        fontSize: '16px',
                                        letterSpacing: '0.3px'
                                    }}>Location:</h5>
                                    <div style={{
                                        display: 'inline-block',
                                        padding: '6px 14px',
                                        backgroundColor: '#e3f2fd',
                                        border: '1px solid #90caf9',
                                        borderRadius: '4px',
                                        color: '#1565c0',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        minWidth: '80px',
                                        textAlign: 'center'
                                    }}>
                                        {locationDescription}
                                    </div>
                                    <h5 style={{ 
                                        margin: 0, 
                                        fontWeight: 600, 
                                        color: '#2c3e50', 
                                        fontSize: '16px',
                                        letterSpacing: '0.3px',
                                        marginLeft: '10px'
                                    }}>Row:</h5>
                                    <div style={{
                                        display: 'inline-block',
                                        padding: '6px 14px',
                                        backgroundColor: '#f3e5f5',
                                        border: '1px solid #ce93d8',
                                        borderRadius: '4px',
                                        color: '#7b1fa2',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        minWidth: '50px',
                                        textAlign: 'center'
                                    }}>
                                        {row}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <Button 
                                        bsStyle="primary" 
                                        onClick={() => this.handleSelectAll()}
                                        style={{ minWidth: '100px' }}
                                    >
                                        {selectedCells.size === levels.length * pallets.length ? 'Deselect All' : 'Select All'}
                                    </Button>
                                    <Button 
                                        bsStyle="success" 
                                        onClick={() => this.handleSave()}
                                        style={{ minWidth: '100px' }}
                                        disabled={pendingSelections.size === 0}
                                    >
                                        Save ({pendingSelections.size})
                                    </Button>
                                </div>
                            </div>
                            <div className={SHARED_GRID_TABLE_SCROLL_WRAP_CLASS} style={{ overflowX: 'auto' }}>
                                <table style={{ 
                                    width: '100%', 
                                    borderCollapse: 'collapse',
                                    border: '1px solid #dee2e6'
                                }}>
                                    <thead>
                                        <tr>
                                            <th style={{ 
                                                padding: '8px', 
                                                border: '1px solid #dee2e6',
                                                backgroundColor: '#f8f9fa',
                                                minWidth: '80px',
                                                textAlign: 'center'
                                            }}>Level / Pallet</th>
                                            {pallets.map(pallet => {
                                                // Check if all cells in this column are selected
                                                const columnCells = levels.map(level => `${level}-${pallet}`);
                                                const allColumnSelected = columnCells.every(cellKey => selectedCells.has(cellKey));
                                                
                                                return (
                                                    <th 
                                                        key={pallet} 
                                                        onClick={() => this.handleColumnClick(pallet)}
                                                        style={{ 
                                                            padding: '8px', 
                                                            border: '1px solid #dee2e6',
                                                            backgroundColor: '#f8f9fa',
                                                            color: '#000000',
                                                            minWidth: '80px',
                                                            textAlign: 'center',
                                                            fontWeight: 'bold',
                                                            cursor: 'pointer',
                                                            transition: 'background-color 0.2s',
                                                            userSelect: 'none'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.backgroundColor = '#e9ecef';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.backgroundColor = '#f8f9fa';
                                                        }}
                                                        title={`Click to ${allColumnSelected ? 'deselect' : 'select'} entire column`}
                                                    >
                                                        {pallet}
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {levels.map(level => {
                                            // Check if all cells in this row are selected
                                            const rowCells = pallets.map(pallet => `${level}-${pallet}`);
                                            const allRowSelected = rowCells.every(cellKey => selectedCells.has(cellKey));
                                            
                                            return (
                                                <tr key={level}>
                                                    <td 
                                                        onClick={() => this.handleRowClick(level)}
                                                        style={{ 
                                                            padding: '8px', 
                                                            border: '1px solid #dee2e6',
                                                            backgroundColor: '#f8f9fa',
                                                            color: '#000000',
                                                            textAlign: 'center',
                                                            fontWeight: 'bold',
                                                            cursor: 'pointer',
                                                            transition: 'background-color 0.2s',
                                                            userSelect: 'none'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.backgroundColor = '#e9ecef';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.backgroundColor = '#f8f9fa';
                                                        }}
                                                        title={`Click to ${allRowSelected ? 'deselect' : 'select'} entire row`}
                                                    >
                                                        Level {level}
                                                    </td>
                                                {pallets.map(pallet => {
                                                    const cellKey = `${level}-${pallet}`;
                                                    const isSelected = selectedCells.has(cellKey);
                                                    const isPending = pendingSelections.has(cellKey);
                                                    const label = `${area}${row}.${level}.${pallet}`;
                                                    
                                                    // Determine background color: saved (blue), pending (yellow), none (white)
                                                    let backgroundColor = '#ffffff';
                                                    let color = '#000000';
                                                    if (isSelected) {
                                                        backgroundColor = '#007bff';
                                                        color = '#ffffff';
                                                    } else if (isPending) {
                                                        backgroundColor = '#ffc107';
                                                        color = '#000000';
                                                    }
                                                    
                                                    return (
                                                        <td
                                                            key={pallet}
                                                            onClick={() => this.handleCellClick(level, pallet)}
                                                            style={{
                                                                padding: '10px',
                                                                border: '1px solid #dee2e6',
                                                                textAlign: 'center',
                                                                cursor: 'pointer',
                                                                backgroundColor: backgroundColor,
                                                                color: color,
                                                                minWidth: '80px',
                                                                transition: 'background-color 0.2s',
                                                                position: 'relative'
                                                            }}
                                                            title={label}
                                                        >
                                                            <div style={{ fontSize: '11px', fontWeight: '500' }}>
                                                                {label}
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export { ActivateStorage };
